import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faQuestionCircle, 
  faPlusCircle, 
  faSignInAlt,
  faShieldAlt,
  faHome,
  faChartLine,
  faIdCard,
  faHouseLaptop,
  faTools,
  faTemperatureArrowUp,
  faCoins,
  faHandshake,
  faSearchDollar,
  faFileAlt,
  faUpload,
  faBadgeCheck,
  faBullhorn,
  faInbox,
  faCalculator,
  faExclamationTriangle,
  faLocationDot
} from '@fortawesome/free-solid-svg-icons';
import './HomePage.css';
import Testimonials from './Testimonials';
import CTASection from './CTASection';
import Footer from '../Footer/FooterWrapper'
import GuideSection from './GuideSection';
import PriceGraph from './PriceGraph';
import SocialMedia from './SocialMedia';
import { trackFormSubmission, isBodexEsHost } from '../../utils/analytics';
import config from '../../config';
import axios from '../../axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';
import { isBodexDkHostname, isBodexNoHostname } from '../../utils/spanishMarket';

// Use the Mapbox token from config.js
const MAPBOX_TOKEN = config.mapbox_token;

function HomePage() {
  const history = useHistory();
  const { t, language } = useLanguage();
  const [heroAddress, setHeroAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [selectedFromSuggestion, setSelectedFromSuggestion] = useState(false);
  const [addressError, setAddressError] = useState('');
  const isNorwegianHost = typeof window !== 'undefined'
    && isBodexNoHostname(window.location.hostname);
  const isDanishHost = typeof window !== 'undefined'
    && isBodexDkHostname(window.location.hostname);
  // True only on bodex.no/spania (Norwegian host + Spanish alias path).
  const isNorwegianSpanishAlias = isNorwegianHost && isBodexEsHost();
  // True only on bodex.dk/spanien (Danish host + Spanish alias path).
  const isDanishSpanishAlias = isDanishHost && isBodexEsHost();
  const heroTitleKey = isDanishSpanishAlias
    ? 'home.heroTitleDkEs'
    : (isNorwegianSpanishAlias
    ? 'home.heroTitleNoEs'
    : (isNorwegianHost
      ? 'home.heroTitleNo'
      : (isBodexEsHost() ? 'home.heroTitleEs' : 'home.heroTitle')));
  const heroSubtitleKey = isDanishSpanishAlias
    ? 'home.heroSubtitleDkEs'
    : (isNorwegianSpanishAlias
    ? 'home.heroSubtitleNoEs'
    : (isBodexEsHost() ? 'home.heroSubtitleEs' : 'home.heroSubtitle'));
  const heroValueButtonKey = isDanishSpanishAlias
    ? 'home.heroValueButtonDkEs'
    : (isNorwegianSpanishAlias
    ? 'home.heroValueButtonNoEs'
    : (isBodexEsHost() ? 'home.heroValueButtonEs' : 'common.value'));

  // Function to track address input
  const trackAddressInput = async (address, pageSource) => {
    try {
      await axios.post(`${config.backend_url}/api/track-address`, {
        address,
        page_source: pageSource
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Address tracking failed:', error);
    }
  };

  const handleTrackClickHero = () => {
    trackFormSubmission('ClickToObjectForm_Hero_Slim', {
      event_category: 'Navigation',
      event_label: 'Clicked Link to Slim Object Form from Hero'
    });
  };

  const handleTrackClickLogin = () => {
    trackFormSubmission('ClickToObjectForm_LoginSection_Slim', {
      event_category: 'Navigation',
      event_label: 'Clicked Link to Slim Object Form from Login Section'
    });
  };

  // Handle address change with autocomplete
  const handleHeroAddressChange = async (e) => {
    const value = e.target.value;
    setHeroAddress(value);
    setSelectedFromSuggestion(false);
    setAddressError('');

    // Only search if the value is sufficiently long
    if (value.length > 3) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            value
            )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
        
        // Check if the typed value exactly matches any suggestion
        const exactMatch = data.features.some(feature => 
          feature.place_name === value
        );
        
        if (exactMatch) {
          setSelectedFromSuggestion(true);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  // When a suggestion is clicked, update address and clear suggestions
  const handleSuggestionClick = (feature) => {
    setHeroAddress(feature.place_name);
    setAddressSuggestions([]);
    setSelectedFromSuggestion(true);
    setAddressError('');
  };

  const handleHeroFormSubmit = (e) => {
    e.preventDefault();
    
    // Validate address
    if (!heroAddress.trim()) {
      setAddressError(t('home.addressRequired'));
      return;
    }

    if (!selectedFromSuggestion) {
      setAddressError(t('home.addressSuggestionRequired'));
      return;
    }

    // Track address input for analytics
    trackAddressInput(heroAddress.trim(), 'homepage');

    // Track form submission
    trackFormSubmission('ClickToObjectForm_Hero', {
      event_category: 'Navigation',
      event_label: 'Submitted Address Form in Hero and Navigated to Valuation'
    });
    
    // Navigate to valuation with address parameter
    history.push(`/valuation?address=${encodeURIComponent(heroAddress.trim())}`);
  };

  const handleCreateAdClick = (e) => {
    e.preventDefault();
    if (!heroAddress.trim()) {
      setAddressError(t('home.addressRequired'));
      return;
    }

    // Track address input for analytics
    trackAddressInput(heroAddress.trim(), 'homepage');

    // For creating an ad, we can be more lenient and not require a suggestion selection,
    // as the user can correct the address in the form.
    handleTrackClickHero();
    history.push(`/object-form?workflow=slim&address=${encodeURIComponent(heroAddress.trim())}`);
  };

  return (
    <>
      <div className="home-page-wrapper">
        <div className="hero-section">
          <div className="hero-content">
            <h1>{t(heroTitleKey)}</h1>
            <p className="hero-subtitle">
              {t(heroSubtitleKey)}
            </p>
{/*             {isBodexEsHost() && (
              <ul className="hero-cities" aria-hidden="true">
                <li>Alicante</li>
                <li>Marbella</li>
                <li>Gran Canaria</li>
                <li>Mallorca</li>
              </ul>
            )} */}
            <form onSubmit={handleHeroFormSubmit} className="hero-form">
              <div className="hero-input-container">
                <div className="hero-address-wrapper">
                  {/* Show suggestions */}
                  {addressSuggestions.length > 0 && (
                    <ul className="suggestions-list-hero">
                      {addressSuggestions.map((feature) => (
                        <li
                          key={feature.id}
                          onClick={() => handleSuggestionClick(feature)}
                        >
                          {feature.place_name}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="hero-address-input-wrapper">
                    <FontAwesomeIcon icon={faLocationDot} className="hero-address-icon" />
                    <input
                      type="text"
                      placeholder={t('home.addressPlaceholder')}
                      value={heroAddress}
                      onChange={handleHeroAddressChange}
                      className={`hero-address-input ${addressError ? 'input-error' : ''}`}
                    />
                  </div>
                  {/* Address validation error */}
                  {addressError && (
                    <div className="hero-address-validation-error">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>{addressError}</span>
                    </div>
                  )}
                </div>
                <button type="submit" className="button primary-button">
                  <FontAwesomeIcon icon={faCalculator} />
                  <span>{t(heroValueButtonKey)}</span>
                </button>
                <button type="button" onClick={handleCreateAdClick} className="button primary-button">
                  <FontAwesomeIcon icon={faPlusCircle} />
                  <span>{t('common.createAd')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>



        <div className="features-section">
          <div className="feature-card">
            <FontAwesomeIcon icon={faHome} className="feature-icon" />
            <h3>{t('home.featureSafeTitle')}</h3>
            <p>{t('home.featureSafeBody')}</p>
          </div>
          <div className="feature-card">
            <FontAwesomeIcon icon={faChartLine} className="feature-icon" />
            <h3>{t('home.featureValueTitle')}</h3>
            <p>{t('home.featureValueBody')}</p>
          </div>
          <div className="feature-card">
            <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
            <h3>{t('home.featureValuationTitle')}</h3>
            <p>{t('home.featureValuationBody')}</p>
          </div>
        </div>

        <Testimonials />


        <div className="login-section">
          <h3>{t('home.startTitle')}</h3>
          <div className="login-buttons">
            <Link to="/object-form?workflow=slim" className="button bankid-button" onClick={handleTrackClickLogin}>
              <FontAwesomeIcon icon={faPlusCircle} />
              <span>{t('common.createAd')}</span>
            </Link>
            <Link to="/faq" className="button bankid-button">
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>{t('common.faq')}</span>
            </Link>
          </div>
        </div>

        <div className="how-it-works-section">
          <div className="how-it-works-content">
            <h2>{t('home.howItWorksTitle')}</h2>
            <div className="steps-grid">
              <div className="step-item">
                <div className="step-number">1</div>
                <FontAwesomeIcon icon={faFileAlt} className="step-icon" />
                <h3>{t('home.stepOneTitle')}</h3>
                <p>{t('home.stepOneBody')}</p>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <FontAwesomeIcon icon={faBullhorn} className="step-icon" />
                <h3>{t('home.stepTwoTitle')}</h3>
                <p>{t('home.stepTwoBody')}</p>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <FontAwesomeIcon icon={faHandshake} className="step-icon" />
                <h3>{t('home.stepThreeTitle')}</h3>
                <p>{t('home.stepThreeBody')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* <SocialMedia /> */}
        
        {/* <PriceGraph /> */}

        {/* <GuideSection /> */}

        <CTASection />
      </div>
      <Footer />
    </>
  );
}

export default HomePage;
